import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { AdminOverview, AdminUsersSort } from '@b4b/shared';
import { useAdminUsers } from '../../api/queries.js';
import { formatDecimal } from './format.js';

const SORTS: AdminUsersSort[] = ['lastActive', 'reads', 'created'];

export default function UsersTable({ overview }: { overview: AdminOverview }) {
  const { t, i18n } = useTranslation();
  const [sort, setSort] = useState<AdminUsersSort>('lastActive');
  const users = useAdminUsers(sort);
  const fmtDate = (iso: string | null, never: string) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short' }).format(new Date(iso)) : never;
  const fmtDateTime = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso)) : t('admin.users.never');

  return (
    <section className="space-y-3 rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-semibold">{t('admin.users.title')}</h2>
        <div role="group" aria-label={t('admin.users.title')} className="flex rounded-xl bg-neutral-100 p-1 dark:bg-neutral-800">
          {SORTS.map((s) => (
            <button key={s} type="button" onClick={() => setSort(s)} aria-pressed={sort === s} className={`min-h-[44px] flex-1 rounded-lg px-3 text-xs ${sort === s ? 'bg-white font-medium shadow-sm dark:bg-neutral-700' : 'text-neutral-500'}`}>
              {t(`admin.users.sort.${s}`)}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-neutral-500">{t('admin.users.summary', overview.users)}</p>

      {users.isPending ? <p className="text-sm text-neutral-500">{t('common.loading')}</p> : null}
      {users.data ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="text-left text-xs text-neutral-500">
              <tr>
                <th className="py-1">{t('admin.users.col.email')}</th>
                <th className="py-1">{t('admin.users.col.lastActive')}</th>
                <th className="py-1">{t('admin.users.col.created')}</th>
                <th className="py-1">{t('admin.users.col.premium')}</th>
                <th className="py-1 text-right">{t('admin.users.col.feeds')}</th>
                <th className="py-1 text-right">{t('admin.users.col.devices')}</th>
                <th className="py-1 text-right">{t('admin.users.col.readsToday')}</th>
                <th className="py-1 text-right">{t('admin.users.col.readsAvg')}</th>
                <th className="py-1 text-right">{t('admin.users.col.saved')}</th>
                <th className="py-1 text-right">{t('admin.users.col.translations')}</th>
              </tr>
            </thead>
            <tbody>
              {users.data.items.map((u) => (
                <tr key={u.id} className="border-t border-neutral-100 dark:border-neutral-800">
                  <td className="py-2"><Link to={`/admin/users/${u.id}`} className="text-accent underline-offset-2 hover:underline">{u.email}</Link></td>
                  <td className="py-2 whitespace-nowrap">{fmtDateTime(u.lastActiveAt)}</td>
                  <td className="py-2 whitespace-nowrap">{fmtDate(u.createdAt, '')}</td>
                  <td className="py-2 whitespace-nowrap">{fmtDate(u.premiumUntil, t('admin.users.noPremium'))}</td>
                  <td className="py-2 text-right tabular-nums">{u.feeds}</td>
                  <td className="py-2 text-right tabular-nums">{u.devices}</td>
                  <td className="py-2 text-right tabular-nums">{u.readsToday}</td>
                  <td className="py-2 text-right tabular-nums">{formatDecimal(u.readsPerDay30, i18n.language)}</td>
                  <td className="py-2 text-right tabular-nums">{u.saved}</td>
                  <td className="py-2 text-right tabular-nums">{u.translationsRequested30}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
